import React from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  TextField,
  Typography,
  Stack,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";

const THUMB_SIZE = 48;
const MAX_THUMBS = 6;

/**
 * Pick small preview URLs (newest groups first, walk poses).
 */
export function pickProgressPhotoPreviewUrls(groupedPhotos, max = MAX_THUMBS) {
  if (!groupedPhotos?.length) return [];
  const urls = [];
  for (const g of groupedPhotos) {
    for (const p of g.photos || []) {
      if (p?.url && !urls.includes(p.url)) {
        urls.push(p.url);
        if (urls.length >= max) return urls;
      }
    }
  }
  return urls;
}

/**
 * Compact client rows: name + count first (full width), thumbnails behind
 * per-card Reveal (privacy), tap card to open detail.
 */
const MediaProgressPhotosClientSummaryList = ({
  sections,
  getClientDisplayName,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState("");
  const [revealedClientIds, setRevealedClientIds] = React.useState(
    () => new Set(),
  );

  const sortedAndFiltered = React.useMemo(() => {
    const withNames = sections.map((s) => ({
      ...s,
      displayName: getClientDisplayName(s.clientId),
    }));
    withNames.sort((a, b) =>
      a.displayName.localeCompare(b.displayName, undefined, {
        sensitivity: "base",
      }),
    );
    const q = query.trim().toLowerCase();
    if (!q) return withNames;
    return withNames.filter(
      (s) =>
        s.displayName.toLowerCase().includes(q) ||
        s.clientId.toLowerCase().includes(q),
    );
  }, [sections, getClientDisplayName, query]);

  const sectionClientKey = React.useMemo(
    () =>
      sections
        .map((s) => s.clientId)
        .sort()
        .join("|"),
    [sections],
  );

  // Drop reveal state when the client set changes (tab reload / org switch)
  React.useEffect(() => {
    setRevealedClientIds(new Set());
  }, [sectionClientKey]);

  const revealClient = React.useCallback((clientId, event) => {
    event.stopPropagation();
    event.preventDefault();
    setRevealedClientIds((prev) => {
      const next = new Set(prev);
      next.add(clientId);
      return next;
    });
  }, []);

  const hideClient = React.useCallback((clientId, event) => {
    event.stopPropagation();
    event.preventDefault();
    setRevealedClientIds((prev) => {
      const next = new Set(prev);
      next.delete(clientId);
      return next;
    });
  }, []);

  return (
    <Stack spacing={2} sx={{ minWidth: 0, maxWidth: "100%" }}>
      <TextField
        size="small"
        placeholder="Search clients…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />

      {sortedAndFiltered.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No clients match your search.
        </Typography>
      ) : (
        <Grid container spacing={1.5}>
          {sortedAndFiltered.map(({ clientId, groupedPhotos }) => {
            const displayName = getClientDisplayName(clientId);
            const thumbs = pickProgressPhotoPreviewUrls(groupedPhotos);
            const submissions = groupedPhotos.length;
            const photosRevealed = revealedClientIds.has(clientId);

            return (
              <Grid item xs={12} sm={6} lg={4} key={clientId}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
                  <CardActionArea
                    onClick={() =>
                      navigate(`/home/shared-videos/progress-photos/${clientId}`)
                    }
                    sx={{
                      height: "100%",
                      alignItems: "stretch",
                      display: "block",
                      textAlign: "left",
                    }}
                  >
                    <CardContent
                      sx={{
                        py: 1.5,
                        px: { xs: 1.5, sm: 2 },
                        "&:last-child": { pb: 1.5 },
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box minWidth={0} flex={1}>
                            <Typography
                              variant="subtitle1"
                              component="div"
                              fontWeight={700}
                              sx={{
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                                lineHeight: 1.3,
                              }}
                            >
                              {displayName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              component="div"
                              sx={{ mt: 0.25 }}
                            >
                              {submissions}{" "}
                              {submissions === 1 ? "submission" : "submissions"}
                            </Typography>
                          </Box>
                          <ChevronRightIcon
                            color="action"
                            sx={{ flexShrink: 0, mt: 0.25 }}
                          />
                        </Stack>

                        {photosRevealed ? (
                          <Stack spacing={0.75}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{
                                width: "100%",
                                minWidth: 0,
                                overflowX: "auto",
                                overflowY: "hidden",
                                pb: 0.25,
                                WebkitOverflowScrolling: "touch",
                              }}
                            >
                              {thumbs.length === 0 ? (
                                <Box
                                  sx={{
                                    width: THUMB_SIZE,
                                    height: THUMB_SIZE,
                                    borderRadius: 1,
                                    bgcolor: "action.hover",
                                    flexShrink: 0,
                                  }}
                                />
                              ) : (
                                thumbs.map((url, i) => (
                                  <Box
                                    key={`${clientId}-t-${i}`}
                                    sx={{
                                      width: THUMB_SIZE,
                                      height: THUMB_SIZE,
                                      borderRadius: 1,
                                      overflow: "hidden",
                                      flexShrink: 0,
                                      border: 1,
                                      borderColor: "divider",
                                    }}
                                  >
                                    <img
                                      src={url}
                                      alt=""
                                      loading="lazy"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                      }}
                                    />
                                  </Box>
                                ))
                              )}
                            </Stack>
                            <Button
                              size="small"
                              variant="text"
                              color="inherit"
                              startIcon={<VisibilityOffIcon />}
                              onClick={(e) => hideClient(clientId, e)}
                              sx={{
                                alignSelf: "flex-start",
                                textTransform: "none",
                                fontWeight: 600,
                                px: 0.5,
                              }}
                            >
                              Hide photos
                            </Button>
                          </Stack>
                        ) : (
                          <Box
                            sx={{
                              py: 1,
                              px: 1.5,
                              borderRadius: 1.5,
                              bgcolor: "grey.50",
                              border: "1px dashed",
                              borderColor: "grey.300",
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.75}
                                minWidth={0}
                              >
                                <VisibilityOffIcon
                                  fontSize="small"
                                  color="action"
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                >
                                  Photos hidden
                                </Typography>
                              </Stack>
                              <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                startIcon={<VisibilityIcon />}
                                onClick={(e) => revealClient(clientId, e)}
                                sx={{
                                  flexShrink: 0,
                                  borderRadius: 20,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  px: 1.5,
                                }}
                              >
                                Reveal
                              </Button>
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Stack>
  );
};

export default MediaProgressPhotosClientSummaryList;
